import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Spin } from 'antd';
import { useSelector } from 'react-redux';
import { selectIsAuthenticated } from '../redux/slices/authSlice';

// 懒加载组件 - 认证布局
const AuthLayout = lazy(() => import('../components/layout/AuthLayout'));

// 懒加载组件 - 认证相关
const LoginPage = lazy(() => import('../pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('../pages/auth/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('../pages/auth/ForgotPasswordPage'));

// 懒加载组件 - 主布局
const MainLayout = lazy(() => import('../components/layout/MainLayout'));

// 懒加载组件 - 首页
const HomePage = lazy(() => import('../pages/HomePage'));

// 懒加载组件 - 存证管理
const EvidenceListPage = lazy(() => import('../pages/evidence/EvidenceListPage'));
const EvidenceCreatePage = lazy(() => import('../pages/evidence/EvidenceCreatePage'));
const EvidenceVerifyPage = lazy(() => import('../pages/evidence/EvidenceVerifyPage'));
const EvidenceVerifyReportPage = lazy(() => import('../pages/evidence/EvidenceVerifyReportPage'));

// 懒加载组件 - 存证证书列表（对应 ql_cz_evidence_verify_report 表）
const CertificateListPage = lazy(() => import('../pages/evidence/CertificateListPage'));

// 懒加载组件 - 公开查验页（fix-verify-public：/verify 与 /verify/:evidenceNo 匿名可达）
const PublicVerifyPage = lazy(() => import('../pages/evidence/PublicVerifyPage'));

// 懒加载组件 - 个人中心
const VerificationPage = lazy(() => import('../pages/user/VerificationPage'));
const AccountSettingsPage = lazy(() => import('../pages/user/AccountSettingsPage'));

// 懒加载组件 - 协议公开页（C7：/agreement 服务协议、/privacy 隐私政策）
const AgreementPage = lazy(() => import('../pages/agreement/AgreementPage'));

// 懒加载组件 - 钱包
const WalletPage = lazy(() => import('../pages/wallet/WalletPage'));
const RechargePage = lazy(() => import('../pages/Recharge/RechargePage'));
const ConsumptionPage = lazy(() => import('../pages/Consumption/ConsumptionPage'));

// 加载中组件
const LoadingFallback = () => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
    }}
  >
    <Spin size="large" tip="加载中..." />
  </div>
);

// 路由守卫组件
const PrivateRoute = ({ children }) => {
  const isAuthenticated = useSelector(selectIsAuthenticated);

  if (!isAuthenticated) {
    return <Navigate to="/auth/login" replace />;
  }

  return children;
};

// 公共路由（已登录也能访问）
const PublicRoute = ({ children }) => {
  return children;
};

/**
 * 应用路由配置
 */
const Router = () => {
  return (
    <BrowserRouter>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* 认证相关路由 - 使用AuthLayout */}
          <Route
            path="/auth"
            element={
              <PublicRoute>
                <AuthLayout />
              </PublicRoute>
            }
          >
            <Route path="login" element={<LoginPage />} />
            <Route path="register" element={<RegisterPage />} />
            <Route path="forgot-password" element={<ForgotPasswordPage />} />
          </Route>

          {/* 主应用路由 */}
          <Route
            path="/"
            element={
              <PrivateRoute>
                <MainLayout />
              </PrivateRoute>
            }
          >
            {/* 首页 */}
            <Route index element={<HomePage />} />

            {/* 存证管理（新） */}
            <Route path="evidence" element={<EvidenceListPage />} />
            <Route path="evidence/list" element={<EvidenceListPage />} />
            <Route path="evidence/create" element={<EvidenceCreatePage />} />
            <Route path="evidence/verify" element={<EvidenceVerifyPage />} />
            <Route path="evidence/report/:evidenceNo" element={<EvidenceVerifyReportPage />} />
            <Route path="evidence/certificate/:evidenceNo" element={<EvidenceVerifyReportPage />} />

            {/* 存证证书列表（对应 ql_cz_evidence_verify_report 表） */}
            <Route path="certificates" element={<CertificateListPage />} />

            {/* 钱包 */}
            <Route path="wallet" element={<WalletPage />} />
            <Route path="recharge" element={<RechargePage />} />
            <Route path="consumption" element={<ConsumptionPage />} />

            {/* 个人中心 */}
            <Route path="profile" element={<Navigate to="/profile/account-settings" replace />} />
            <Route path="profile/account-settings" element={<AccountSettingsPage />} />

            {/* 实名认证（一级路由） */}
            <Route path="verification" element={<VerificationPage />} />
          </Route>

          {/* 公开路由：扫码/短链匿名查验（fix-verify-public）
              原挂 EvidenceVerifyReportPage（verify-report 私有接口匿名必 401），
              改挂 PublicVerifyPage（GET /verify/result 白名单投影端点）；
              ReportPage 仅保留上方私有路由 /evidence/report/:evidenceNo */}
          <Route path="/verify/:evidenceNo" element={<PublicVerifyPage />} />
          <Route path="/verify" element={<PublicVerifyPage />} />

          {/* 公开路由：服务协议/隐私政策/充值服务协议（不需要登录，页脚/登录/注册/充值页入口直达）
              T8：保留 /agreement、/privacy 兼容既有调用方（SiteFooter/登录/注册），
              新增 /agreement/recharge 充值协议；未知 type 由 AgreementPage 内 404 兜底 */}
          <Route path="/agreement" element={<AgreementPage type="user-service" />} />
          <Route path="/agreement/recharge" element={<AgreementPage type="recharge" />} />
          <Route path="/privacy" element={<AgreementPage type="privacy" />} />

          {/* 404 页面 */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};

export default Router;
